// Waiting on somebody — the ladder, in the browser (busy-timeline-srv#26, AI Brain spec §2).
//
// "Waiting on a reply" used to borrow the TASK due ladder: a thread with a due date read "Due soon"
// or "Overdue" like a piece of work the user owed. It is the opposite — the next move belongs to
// somebody else — so it has its own three rungs, its own words, and its own window.
//
// Deliberately standalone (no import from ./dueStatus): dueStatus imports this file for the chip the
// `waiting` status wears, and a cycle between the two would leave whichever loaded first holding an
// undefined constant. The two small date helpers below are the price of that, and they are the whole
// of it.
//
// The SERVER decides the rung and sends it with each row (`state`), for the same reason the BFF does
// not: one clock. Everything here is for rows that arrived without one — a thread whose only waiting
// signal is the legacy `waitingOn` + `dueDate` pair.

// The chip for each rung. Colours are the ones the estate already uses (lib/dueStatus): the overdue
// red, the due-soon amber, the waiting indigo and the completed green — a waiting row is not a new
// palette, it is the same statuses said about somebody else's action.
export const WAITING_META = {
  // The answer is expected inside the window — worth surfacing, not yet a problem.
  expectedSoon: { label: 'Expected soon', color: '#ff8800' },
  // Open, nothing imminent. Keeps the words the app has always used for this chip.
  waiting: { label: 'Waiting on', color: '#3741f3' },
  // The day it was expected by has passed and nothing has arrived.
  overdue: { label: 'Overdue', color: '#ef4444' },
  // Not a rung: the reply came, the user cleared it, or it expired.
  resolved: { label: 'Replied', color: '#22c55e' },
}

const FALLBACK = WAITING_META.waiting

// Two days, matching the service's WaitingLadder.ExpectedSoonWindow — NOT the task ladder's hour.
// An hour's notice on another person's action is not something anybody can act on; two days is.
export const EXPECTED_SOON_MS = 48 * 60 * 60 * 1000

// Backend timestamps are UTC but often serialized without a zone suffix; parsing those as local
// shifts every time by the user's offset. Treat naive as UTC. (Same rule as lib/dueStatus.)
export function parseWaitingDate(d) {
  if (d === null || d === undefined || d === '') return null
  if (d instanceof Date) return isNaN(d.getTime()) ? null : d
  const dt = typeof d === 'string' && !/[zZ]$|[+-]\d{2}:?\d{2}$/.test(d) ? new Date(`${d}Z`) : new Date(d)
  return isNaN(dt.getTime()) ? null : dt
}

/**
 * The rung one waiting row is on, given the date the answer was expected by.
 *
 * `resolvedUtc` wins over everything — a row that was answered is never late, however long after its
 * expected day it is read. An open row with NO expected date is `waiting`: waiting without a date is
 * a real state, not a missing field.
 *
 * @param expectedBy when the reply was expected (ISO string or Date)
 * @param resolvedUtc when it stopped being open, if it has
 * @param now the clock, injectable so every boundary is testable
 */
export function waitingState(expectedBy, resolvedUtc = null, now = Date.now()) {
  if (parseWaitingDate(resolvedUtc)) return 'resolved'
  const dt = parseWaitingDate(expectedBy)
  if (!dt) return 'waiting'
  const left = dt.getTime() - now
  if (left < 0) return 'overdue'
  return left <= EXPECTED_SOON_MS ? 'expectedSoon' : 'waiting'
}

// The chip (label + colour) for a rung; anything unknown reads as a plain wait.
export function waitingMeta(state) {
  return WAITING_META[state] || FALLBACK
}

/**
 * The rung of a row as it arrived from the API. A row the service answered carries its own `state`
 * and that is used unchanged — the browser must never second-guess it, because the two clocks would
 * disagree at exactly the boundary the user is looking at. Anything else (a plain thread with the
 * legacy `waitingOn` + `dueDate` pair) is put on the ladder here.
 */
export function rowWaitingState(row, now = Date.now()) {
  if (!row) return null
  if (row.state && WAITING_META[row.state]) return row.state
  if (row.expectedByUtc !== undefined || row.resolvedUtc !== undefined)
    return waitingState(row.expectedByUtc, row.resolvedUtc, now)
  if (row.waitingOn) return waitingState(row.dueDate, null, now)
  return null
}

/**
 * The sentence a row says, for a tooltip or a screen reader: "Waiting on Anna · expected 12 Sep",
 * "Overdue · expected 3 Sep", "Waiting on a reply" when nothing more is known. Short by design.
 */
export function waitingText(row, now = Date.now()) {
  const state = rowWaitingState(row, now)
  if (!state) return ''
  if (state === 'resolved') return 'Replied'
  // The `waiting` chip's label already ENDS in "on" ("Waiting on"), so what follows is the object of
  // that "on" and must not repeat it — otherwise the strip reads "Waiting on on Anna Sokolova".
  const who = row?.ownerName ? ` ${row.ownerName}` : ' a reply'
  const dt = parseWaitingDate(row?.expectedByUtc ?? row?.dueDate)
  const when = dt ? ` · expected ${formatDay(dt)}` : ''
  return state === 'overdue' ? `Overdue${when}` : `${waitingMeta(state).label}${state === 'waiting' ? who : ''}${when}`
}

// "12 Sep" — the shortest form that is still unambiguous in a row that is already dense.
function formatDay(dt) {
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/**
 * The five actions the spec names on a waiting row, in its order (§Appendix 6). Data rather than
 * markup so the list, the row menu and any future surface offer the same five in the same order —
 * the drift this file exists to prevent.
 *
 * `addToOngoing` is the relationship graph's existing verb; it is listed here only so the menu is
 * complete, and the component wires it to the flow that already exists.
 */
export const WAITING_ACTIONS = [
  { key: 'open', label: 'Open' },
  { key: 'followUp', label: 'Follow up' },
  { key: 'remind', label: 'Remind me' },
  { key: 'addToOngoing', label: 'Add to Ongoing' },
  { key: 'clear', label: 'No longer waiting' },
]

/**
 * The three answers to "remind me when?" that cover almost every chase: tomorrow, in three days,
 * next week — each at 09:00 local, because a reminder is something to see at the start of a day.
 *
 * Pure and clock-injected: the one thing that can be wrong here is a reminder in the past, and this
 * way it can be shown not to be.
 */
export function remindChoices(now = new Date()) {
  const base = now instanceof Date ? now : new Date(now)
  const at = days => {
    const d = new Date(base.getTime())
    d.setDate(d.getDate() + days)
    d.setHours(9, 0, 0, 0)
    return d
  }
  return [
    { key: 'tomorrow', label: 'Tomorrow', at: at(1) },
    { key: 'threeDays', label: 'In 3 days', at: at(3) },
    { key: 'nextWeek', label: 'Next week', at: at(7) },
  ]
}

/**
 * Server rows indexed by the subject they are about, so a thread listing can ask "is this one
 * waited on, and how late is it?" without a call per row. Later rows win, which is what an open row
 * arriving after a resolved one for the same subject means.
 */
export function waitingBySubject(rows) {
  const map = {}
  for (const row of Array.isArray(rows) ? rows : []) {
    if (row?.subjectId) map[String(row.subjectId).toLowerCase()] = row
  }
  return map
}

/** How many of these rows are open (the Briefing's "Waiting for replies" count). */
export function openWaitingCount(rows) {
  return (Array.isArray(rows) ? rows : []).filter(r => r && !r.resolvedUtc).length
}

/** How many are overdue — the half of the count that is actually a problem. */
export function overdueWaitingCount(rows, now = Date.now()) {
  return (Array.isArray(rows) ? rows : []).filter(r => rowWaitingState(r, now) === 'overdue').length
}
